import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, API, getToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import NavBar from "../components/NavBar";

const STATUS_BADGE_CLASS = {
  paid: "badge-paid",
  released: "badge-released",
  report_ready: "badge-ready",
  intake_received: "badge-default",
  pending_payment: "badge-pending",
};
const statusBadgeClass = (status) => STATUS_BADGE_CLASS[status] || "badge-default";

const INTAKE_BY_CATEGORY = {
  diagnostic: [
    ["device_type", "Device type (PC / Server / Embedded / Other)", "input"],
    ["manufacturer_model", "Manufacturer & model", "input"],
    ["symptoms", "Symptoms (what is it doing?)", "textarea"],
    ["error_logs", "Error codes / log excerpts", "textarea"],
    ["when_started", "When did the issue start?", "input"],
    ["already_tried", "Anything already tried?", "textarea"],
  ],
  sop: [
    ["device_type", "Hardware / system name", "input"],
    ["audience", "Audience (Technicians / Non-technical)", "input"],
    ["current_process", "Current process (bullet points OK)", "textarea"],
    ["safety_concerns", "Known risks or safety concerns", "textarea"],
    ["notes", "Other notes / source material", "textarea"],
  ],
  optimization: [
    ["hardware_inventory", "Hardware inventory (paste a list / spreadsheet content)", "textarea"],
    ["average_age", "Average hardware age", "input"],
    ["pain_points", "Known pain points (cost, downtime, power, failures)", "textarea"],
    ["desired_outcome", "Desired outcome (cut costs / reliability / both)", "input"],
    ["notes", "Anything else worth knowing", "textarea"],
  ],
};

const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1024*1024 ? `${(n/1024).toFixed(1)} KB` : `${(n/(1024*1024)).toFixed(1)} MB`;

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [intake, setIntake] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upsellBusy, setUpsellBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => {
      setErr(e?.response?.data?.detail || "Order not found");
    });
  }, [id]);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line
  }, [user, id]);

  // Auto-poll while waiting on report (regular intake or upsell auto-gen)
  useEffect(() => {
    if (!order) return;
    if (order.status === "intake_received" || (order.category === "upsell" && order.status === "paid")) {
      const t = setInterval(load, 5000);
      return () => clearInterval(t);
    }
  }, [order, load]);

  const submitIntake = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post(`/orders/${id}/intake`, intake);
      load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to submit intake");
    } finally { setSaving(false); }
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true); setErr("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await fetch(`${API}/orders/${id}/files`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: fd,
        }).then(async (r) => {
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            throw new Error(body?.detail || `Upload failed (${r.status})`);
          }
        });
      }
      await load();
    } catch (e2) {
      setErr(e2?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (fileId) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/orders/${id}/files/${fileId}`);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to remove file");
    }
  };

  const buyUpsell = async () => {
    setUpsellBusy(true); setErr("");
    try {
      const created = await api.post(`/orders/${id}/upsell-prevention`);
      const co = await api.post(`/orders/${created.data.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = co.data.url;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create upsell");
      setUpsellBusy(false);
    }
  };

  if (!user) return null;
  if (err && !order) return (
    <div><NavBar /><div className="max-w-3xl mx-auto px-6 py-20" data-testid="order-error">{err}</div></div>
  );
  if (!order) return (
    <div><NavBar /><div className="max-w-3xl mx-auto px-6 py-20 ribbon" data-testid="order-loading">Loading…</div></div>
  );

  const fields = INTAKE_BY_CATEGORY[order.category] || [];
  const isUpsell = order.category === "upsell";
  const needsIntake = order.status === "paid" && !isUpsell;
  const reportReleased = order.report && order.report.released && order.report.content;
  const reportPendingRelease = order.report && !order.report.released;
  const files = order.files || [];
  const remainingSlots = Math.max(0, 5 - files.length);
  const canShowUpsell = order.category === "diagnostic" && reportReleased;

  return (
    <div data-testid="order-detail-page">
      <NavBar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/dashboard" className="ribbon underline" data-testid="order-back">← Back to dashboard</Link>
        <div className="mt-4 mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow">ORDER / {order.id.slice(0,8)}{isUpsell && " · ADD-ON"}</div>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mt-2">
              {order.service_name}
            </h1>
            <div className="ribbon text-black/60 mt-1">
              £{order.amount.toFixed(2)} · {new Date(order.created_at).toLocaleString()}
            </div>
          </div>
          <span className={`badge ${statusBadgeClass(order.status)}`} data-testid="order-status">{order.status.replace("_", " ")}</span>
        </div>

        {order.status === "pending_payment" && (
          <div className="tech-card p-6 mb-8" data-testid="order-await-payment">
            <p>This order has not been paid yet. Return to the dashboard to complete checkout.</p>
          </div>
        )}

        {needsIntake && (
          <>
            {/* File uploads */}
            <div className="tech-card p-6 mb-6" data-testid="files-section">
              <div className="label-eyebrow">ATTACHMENTS / OPTIONAL</div>
              <h2 className="font-display text-2xl font-bold uppercase mt-1">Upload supporting files</h2>
              <p className="text-sm text-black/70 mt-2">
                Logs, photos, schematics, spreadsheets. Up to 5 files, 10&nbsp;MB each.
                Text files (.log .txt .json .csv .md) are read by the AI for richer analysis.
              </p>
              {files.length > 0 && (
                <ul className="mt-4 divide-y divide-black/15 border border-black/15" data-testid="files-list">
                  {files.map((f) => (
                    <li key={f.file_id} className="flex items-center justify-between p-3"
                        data-testid={`file-row-${f.file_id}`}>
                      <div>
                        <div className="font-mono text-sm">{f.filename}</div>
                        <div className="ribbon text-black/60">{f.content_type} · {fmtSize(f.size)}</div>
                      </div>
                      <button onClick={() => removeFile(f.file_id)}
                              className="ribbon underline hover:text-red-700"
                              data-testid={`file-remove-${f.file_id}`}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4 flex items-center gap-3">
                <input ref={fileInputRef} type="file" multiple onChange={onFilesPicked}
                       disabled={uploading || remainingSlots === 0}
                       className="input" data-testid="file-input" />
                <span className="ribbon text-black/60">
                  {uploading ? "Uploading…" : `${remainingSlots} slot${remainingSlots === 1 ? "" : "s"} left`}
                </span>
              </div>
            </div>

            <form onSubmit={submitIntake} className="tech-card p-6 mb-8 space-y-4" data-testid="intake-form">
              <div className="label-eyebrow">INTAKE / {order.category.toUpperCase()}</div>
              <h2 className="font-display text-2xl font-bold uppercase">Submit your details</h2>
              <p className="text-sm text-black/70">
                The more detail you provide, the sharper the report. All inputs are confidential.
              </p>
              {fields.map(([key, label, kind]) => (
                <div key={key}>
                  <label className="ribbon block mb-2">{label}</label>
                  {kind === "textarea" ? (
                    <textarea className="textarea" value={intake[key] || ""}
                              onChange={(e) => setIntake({ ...intake, [key]: e.target.value })}
                              data-testid={`intake-${key}`} />
                  ) : (
                    <input className="input" value={intake[key] || ""}
                           onChange={(e) => setIntake({ ...intake, [key]: e.target.value })}
                           data-testid={`intake-${key}`} />
                  )}
                </div>
              ))}
              {err && <div className="text-sm text-red-700 border border-red-700 p-2" data-testid="intake-error">{err}</div>}
              <button className="btn btn-primary" disabled={saving} data-testid="intake-submit">
                {saving ? "Submitting…" : "Submit for AI report →"}
              </button>
            </form>
          </>
        )}

        {isUpsell && order.status === "paid" && (
          <div className="tech-card p-6 mb-8" data-testid="upsell-processing">
            <div className="label-eyebrow mb-2">PROCESSING ADD-ON</div>
            <h2 className="font-display text-2xl font-bold uppercase">Generating your prevention checklist…</h2>
            <p className="text-sm text-black/70 mt-2">
              No intake needed — the checklist is being built directly from your diagnostic report. Auto-refreshing.
            </p>
          </div>
        )}

        {order.status === "intake_received" && (
          <div className="tech-card p-6 mb-8" data-testid="intake-processing">
            <div className="label-eyebrow mb-2">PROCESSING</div>
            <h2 className="font-display text-2xl font-bold uppercase">AI engineer drafting your report…</h2>
            <p className="text-sm text-black/70 mt-2">
              This usually completes in under a minute. Auto-refreshing every 5s.
            </p>
          </div>
        )}

        {reportPendingRelease && !reportReleased && order.status !== "released" && (
          <div className="tech-card p-6 mb-8" data-testid="report-in-review">
            <div className="label-eyebrow mb-2">IN REVIEW</div>
            <h2 className="font-display text-2xl font-bold uppercase">Your report is being reviewed.</h2>
            <p className="text-sm text-black/70 mt-2">
              An engineer is verifying the draft. You'll be able to download it once released.
              Generated at {new Date(order.report.generated_at).toLocaleString()}.
            </p>
          </div>
        )}

        {reportReleased && (
          <div className="tech-card p-0 overflow-hidden" data-testid="report-content">
            <div className="bg-black text-white p-4 flex items-center justify-between">
              <div>
                <div className="ribbon text-white/60">REPORT · {order.category.toUpperCase()}</div>
                <div className="font-display font-bold uppercase">{order.service_name}</div>
              </div>
              <button onClick={() => window.print()} className="btn btn-sm btn-outline"
                      style={{ background: "white", color: "black" }}
                      data-testid="report-print">Print / Save PDF</button>
            </div>
            <div className="p-8 prose-report">
              <ReactMarkdown>{order.report.content}</ReactMarkdown>
              <hr className="my-8 border-black/20" />
              <p className="text-xs text-black/60 font-mono">
                Validation notice: all recommendations are advisory and must be validated in
                your environment before implementation. No liability for data loss, downtime
                or damage.
              </p>
            </div>
          </div>
        )}

        {canShowUpsell && (
          <div className="mt-8 tech-card p-6 bg-[color:var(--c-bg-alt)]" data-testid="upsell-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="label-eyebrow">RECOMMENDED ADD-ON · £49</div>
                <h2 className="font-display text-2xl font-bold uppercase mt-1">Prevention &amp; Maintenance Checklist</h2>
                <p className="text-sm text-black/70 mt-2 max-w-xl">
                  Get a tailored daily/weekly/monthly checklist generated directly from this diagnostic.
                  Catch the same failure mode before it happens next time. Auto-delivered in ~24h.
                </p>
              </div>
              <button onClick={buyUpsell} disabled={upsellBusy}
                      className="btn btn-primary" data-testid="upsell-buy-btn">
                {upsellBusy ? "..." : "Add for £49 →"}
              </button>
            </div>
            {err && <div className="mt-3 text-sm text-red-700 border border-red-700 p-2" data-testid="upsell-error">{err}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
